import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { GoogleButton, googleRedirect, takeSessionId } from "@/components/GoogleButton";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Spin } from "@/components/common";

const HERO = "https://static.prod-images.emergentagent.com/jobs/0c8a379b-c978-4bc5-b007-00b9c9c84fe2/images/c65cf008468ace24e910cb5bcda7c036bbd88da36683f9b3b7edd92b1ad90e9f.jpeg";

export default function Login() {
  const { user, login, loginWithGoogle } = useAuth();
  const loc = useLocation();
  const done = useRef(false);
  const nav = useNavigate();
  const [u, setU] = useState(localStorage.getItem("zor_user") || "");
  const [p, setP] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (done.current) return;
    const sid = takeSessionId(loc.hash);
    if (!sid) return;
    done.current = true; setBusy(true);
    loginWithGoogle(sid).then(() => nav("/")).catch((ex) => setErr(errMsg(ex))).finally(() => setBusy(false));
  }, [loc.hash, loginWithGoogle, nav]);
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const un = (fd.get("username") || u).toString().trim();
    const pw = (fd.get("password") || p).toString();
    if (!un || !pw) return setErr("Kullanıcı adı ve şifre girin.");
    setBusy(true); setErr("");
    try { await login(un, pw, remember); nav("/"); } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.08fr_1fr] bg-white">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Ada bahçe kapısında" className="absolute inset-0 w-full h-full object-cover scale-105 animate-[fadeUp_1s_ease]" />
        <div className="absolute top-7 left-8 leading-none text-navy bg-white/40 backdrop-blur-sm rounded-md px-3 py-2">
          <div className="font-serif-d text-[30px] font-bold">ZOR</div>
          <div className="font-serif-d text-[12px]">Dijital Yayınevi</div>
        </div>
      </div>
      <div className="relative flex items-center justify-center px-6 bg-gradient-to-br from-white to-[#f7faf9]">
        <svg className="absolute bottom-0 right-0 w-64 opacity-[0.07] text-teal" viewBox="0 0 200 200" fill="currentColor"><path d="M100 10c20 40 60 50 80 90-40-5-60 20-80 90-20-70-40-95-80-90 20-40 60-50 80-90z" /></svg>
        <form onSubmit={submit} className="w-full max-w-[440px] card-z !rounded-2xl p-10 fade-up" data-testid="login-form">
          <h1 className="font-serif-d text-5xl font-bold text-navy">Hoş geldiniz</h1>
          <p className="font-serif-d text-[#6b7a8c] mt-2 mb-8">Dijital yayınevinize giriş yapın.</p>
          <label className="lbl">Kullanıcı adı</label>
          <div className="relative mb-5">
            <Mail size={16} className="absolute left-3 top-3 text-[#5f6f84]" />
            <input name="username" autoCapitalize="none" autoCorrect="off" className="inp !pl-10 !py-2.5" value={u} onChange={(e) => setU(e.target.value)} onInput={(e) => setU(e.currentTarget.value)} placeholder="Kullanıcı adınız" autoComplete="username" data-testid="login-username-input" />
          </div>
          <label className="lbl">Şifre</label>
          <div className="relative mb-4">
            <Lock size={16} className="absolute left-3 top-3 text-[#5f6f84]" />
            <input name="password" className="inp !pl-10 !pr-10 !py-2.5" type={show ? "text" : "password"} value={p} onChange={(e) => setP(e.target.value)} onInput={(e) => setP(e.currentTarget.value)} placeholder="••••••" autoComplete="current-password" data-testid="login-password-input" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3 text-[#8a96a8]" data-testid="toggle-password-btn">{show ? <Eye size={16} /> : <EyeOff size={16} />}</button>
          </div>
          <div className="flex items-center justify-between text-sm mb-6">
            <label className="flex items-center gap-2 text-[#334862] cursor-pointer">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[var(--teal)] w-4 h-4" data-testid="remember-checkbox" /> Beni hatırla
            </label>
            <button type="button" className="text-teal underline underline-offset-2" onClick={() => setErr("Şifre sıfırlama için yöneticinize başvurun.")} data-testid="forgot-password-link">Şifremi unuttum</button>
          </div>
          {err && <div className="mb-4 text-sm text-[#d1453b] bg-[#fdeceb] rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
          <button disabled={busy} className="btn-teal w-full py-3 flex items-center justify-center gap-2" data-testid="login-submit-button">
            {busy ? <Spin /> : <>Giriş yap <ArrowRight size={16} /></>}
          </button>
          <div className="flex items-center gap-3 my-5 text-xs text-[#8a96a8]"><span className="flex-1 h-px bg-[#e3e8ee]" />veya<span className="flex-1 h-px bg-[#e3e8ee]" /></div>
          <GoogleButton onClick={() => googleRedirect("/giris")} label="Google ile giriş yap" testid="google-login-btn" disabled={busy} />
          <div className="border-t mt-8 pt-5 text-center text-xs text-[#8a96a8] font-serif-d">Kurum hesabınızla güvenli giriş.</div>
        </form>
      </div>
    </div>
  );
}
